import { CommandResult, type RawResponse } from "./result"
import { UserService } from "@/lib/auth/keycloak"
import {
  emitIdentitySetupChanged,
  parseIdentitySetupFromPayload,
  type IdentitySetup,
} from "@/lib/identity-setup"

const API_BASE = process.env.NEXT_PUBLIC_API_BASE ?? "http://localhost:8000"

/** When `redirect: "manual"`, cross-origin 303 can surface as `opaqueredirect` (HTTP 0, no `Location` in JS). */
function postSetupRedirectTarget(response: Response, requestUrl: string): string {
  const loc = response.headers.get("Location")
  if (loc != null && loc.trim() !== "") {
    return new URL(loc, requestUrl).href
  }
  const configured = process.env.NEXT_PUBLIC_POST_SETUP_URL?.trim()
  if (configured) {
    return configured
  }
  if (typeof window !== "undefined") {
    return `${window.location.origin}/contacts`
  }
  return "http://localhost:3000/contacts"
}

function propagateIdentitySetupFromBody(body: unknown): void {
  const setup = parseIdentitySetupFromPayload(body)
  if (setup) emitIdentitySetupChanged(setup)
}

async function handleUnauthorizedResponse(response: Response, body: unknown): Promise<void> {
  if (response.status !== 401) return
  if (!body || typeof body !== "object") return

  const logoutRequired = "logout_required" in body && (body as { logout_required?: unknown }).logout_required === true
  const errorCode = "error_code" in body ? (body as { error_code?: unknown }).error_code : undefined
  if (
    logoutRequired ||
    errorCode === "identity_user_inactive" ||
    errorCode === "identity_workspace_inactive"
  ) {
    await UserService.logout()
  }
}

function toSnakeCase(s: string): string {
  return s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`).replace(/^_/, "")
}

/**
 * Fire-and-forget POST for use during page unload. Same wire format as {@link CommandPage}.
 * Prefer normal {@link CommandPage} when the document is still active.
 */
export function postCommandKeepalive(
  pagePath: string,
  commandProp: string,
  data?: Record<string, unknown>
): void {
  const url = `${API_BASE}${pagePath}`
  const body: Record<string, unknown> = { command: toSnakeCase(commandProp) }
  if (data && Object.keys(data).length > 0) body.data = data
  const token = UserService.getToken()
  void fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {})
}

/**
 * A proxy-based command client. Method calls on the returned object become
 * POST requests to the page URL.
 *
 * Usage:
 *   const page = new CommandPage("/contacts")
 *   const result = await page.getContact({ id: "abc" })
 *   const result = await page.search({ query: "foo" }, "extra1", "extra2")
 *   const result = await page.search({ query: "foo", _args: ["extra1"] })
 */
export function CommandPage(pagePath: string): CommandPageProxy {
  const url = `${API_BASE}${pagePath}`

  return new Proxy({} as CommandPageProxy, {
    get(_target, prop: string | symbol) {
      if (prop === "url") return url
      // Async helpers that `return CommandPage(...)` would otherwise be assimilated as
      // thenables (Promise.resolve(proxy) calls proxy.then), posting command "then".
      if (prop === "then" || prop === "catch" || prop === "finally") {
        return undefined
      }
      if (typeof prop !== "string") {
        return undefined
      }

      return async (
        inputOrFirstArg?: Record<string, unknown> | unknown,
        ...rest: unknown[]
      ): Promise<CommandResult> => {
        let data: Record<string, unknown> | undefined
        let args: unknown[] | undefined

        if (
          inputOrFirstArg !== null &&
          inputOrFirstArg !== undefined &&
          typeof inputOrFirstArg === "object" &&
          !Array.isArray(inputOrFirstArg)
        ) {
          const input = { ...(inputOrFirstArg as Record<string, unknown>) }
          if ("_args" in input) {
            args = input._args as unknown[]
            delete input._args
          }
          if (rest.length > 0) {
            args = [...(args ?? []), ...rest]
          }
          data = Object.keys(input).length > 0 ? input : undefined
        } else if (inputOrFirstArg !== undefined) {
          args = [inputOrFirstArg, ...rest]
        }

        const requestBody: Record<string, unknown> = { command: toSnakeCase(prop) }
        if (args && args.length > 0) requestBody.args = args
        if (data) requestBody.data = data

        const isIdentitySession = url.includes("/identity/session")
        const sessionData = requestBody.data as Record<string, unknown> | undefined
        const postedComplete = Boolean(sessionData?.complete)
        const isSessionComplete =
          isIdentitySession &&
          requestBody.command === "session" &&
          postedComplete

        const token = await UserService.getValidToken()
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
          body: JSON.stringify(requestBody),
          // Never auto-follow 303 from post-setup: the follow-up GET is HTML/empty and breaks `json()`.
          redirect: isIdentitySession ? "manual" : "follow",
        })

        const rawText = await response.text()
        const status = response.status
        const trimmed = rawText.trim()
        const isRedirectStatus = status >= 301 && status <= 308
        // Cross-origin 303 + `redirect: "manual"` often yields HTTP 0, empty body, no readable `Location`
        // (`response.type === "opaqueredirect"`). Same treatment as a visible 3xx redirect.
        const isRedirectHiddenFromJs =
          status === 0 && trimmed === "" && response.type !== "error"

        if (isSessionComplete && (isRedirectStatus || isRedirectHiddenFromJs)) {
          const done: IdentitySetup = {
            required: false,
            workspace: false,
            user: false,
          }
          queueMicrotask(() =>
            propagateIdentitySetupFromBody({
              result: "ok",
              data: { setup: done },
            })
          )
          const target = postSetupRedirectTarget(response, url)
          if (typeof window !== "undefined") {
            window.location.assign(target)
          }
          return CommandResult.fromResponse({
            result: "ok",
            data: { setup: done },
          })
        }

        if (
          isIdentitySession &&
          requestBody.command === "session" &&
          isRedirectStatus &&
          !isSessionComplete
        ) {
          throw new Error(
            "Unexpected HTTP redirect from identity/session; only session(complete: true) should receive a redirect."
          )
        }

        if (!trimmed) {
          if (status === 0 && response.type === "error") {
            throw new Error(
              "Command request failed (network error or CORS). Check NEXT_PUBLIC_API_BASE and backend CORS_ORIGINS."
            )
          }
          throw new Error(`Empty response from command API (HTTP ${status})`)
        }

        let json: unknown
        try {
          json = JSON.parse(trimmed) as unknown
        } catch {
          throw new Error(`Invalid JSON from command API (HTTP ${status})`)
        }
        await handleUnauthorizedResponse(response, json)
        if (response.ok && isIdentitySession) {
          queueMicrotask(() => propagateIdentitySetupFromBody(json))
        } else {
          propagateIdentitySetupFromBody(json)
        }
        return CommandResult.fromResponse(json as RawResponse)
      }
    },
  })
}

/** The proxy type: any property access returns an async command function. */
export type CommandPageProxy = {
  readonly url: string
} & {
  [command: string]: (
    inputOrFirstArg?: Record<string, unknown> | unknown,
    ...rest: unknown[]
  ) => Promise<CommandResult>
}
